import type { ComboboxItem } from '@/components'
import { Button, Combobox, Field } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { FieldPath } from 'react-hook-form'
import {
  Controller,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form'
import type { AnyAbsenceReportSchema, ExportFormat, ExportOption } from './util'

interface HasExports {
  exportOption: ExportOption
  exportFormat: ExportFormat
}

interface Props<
  TValues extends FieldValues & HasExports,
  TOption extends ExportOption,
  TFormat extends ExportFormat,
> {
  form: UseFormReturn<TValues>
  schema: AnyAbsenceReportSchema<TOption, TFormat>
  loading: boolean
}

export function AbsenceReportSubmit<
  TValues extends FieldValues & HasExports,
  TOption extends ExportOption,
  TFormat extends ExportFormat,
>({ form, schema, loading }: Props<TValues, TOption, TFormat>) {
  const translations = useTranslate({
    generateReport: 'settings.absence.reports.generate-report',

    options: {
      MAIL: 'export.options.mail',
      DOWNLOAD: 'export.options.download',
      SAVE: 'export.options.save',
    },
    formats: {
      PDF: 'export.formats.pdf',
      PDF_SALARY: 'export.formats.pdf-salary',
      EXCEL: 'export.formats.excel',
      EXCEL_FLAT: 'export.formats.excel-flat',
      VISMA: 'export.formats.visma',
      VISMA_NEW: 'export.formats.visma-new',
      POWEROFFICE: 'export.formats.poweroffice',
      TWENTYFOUR_SEVEN_OFFICE: 'export.formats.twentyfour-seven-office',
      TRIPLETEX: 'export.formats.tripletex',
      ZIP: 'export.formats.zip',
    },
  })

  const optionOptions = schema.shape.exportOption.options.map<
    ComboboxItem<ExportOption>
  >(option => ({
    value: option,
    label: String(translations.options[option]),
  }))

  const formatOptions = schema.shape.exportFormat.options.map<
    ComboboxItem<ExportFormat>
  >(format => ({
    value: format,
    label: String(translations.formats[format]),
  }))

  return (
    <div className="flex justify-end gap-2">
      <Controller
        control={form.control}
        name={'exportFormat' as FieldPath<TValues>}
        render={({ field }) => (
          <Field>
            <Combobox
              {...field}
              className="h-10 px-3 rounded-md text-sm"
              itemClassName="text-sm whitespace-nowrap"
              items={formatOptions}
              mode="single"
              noSearch
            />
          </Field>
        )}
      />

      <Controller
        control={form.control}
        name={'exportOption' as FieldPath<TValues>}
        render={({ field }) => (
          <Field>
            <Combobox
              {...field}
              className="h-10 px-3 rounded-md text-sm"
              itemClassName="text-sm whitespace-nowrap"
              items={optionOptions}
              mode="single"
              noSearch
            />
          </Field>
        )}
      />

      <Button type="submit" size="lg" disabled={loading}>
        {translations.generateReport}
      </Button>
    </div>
  )
}
